import { ConfirmationModalComponent, PreventModalComponent } from 'commons/components';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { useTranslation } from 'core/i18n/i18n';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { updateQuery } from 'core/query-client/query-client';
import { executeRequest } from 'core/utils/backend-service';
import { showErrorNotification, showSuccessNotification } from 'core/utils/notification-service';
import { EapSettings } from 'modules/settings/pods/settings-devices/settings-devices-menu.model';
import { usePopupManager } from 'react-popup-manager';
import { deleteEapSettings } from 'modules/settings/pods/settings-devices/settings-devices-eap-settings/service/eap-settings.service';
import { DeleteEapSettingHeader } from './delete-eap-setting-header.component';
import {
    getDeleteDescriptionTranslation,
    getPreventDeleteDescriptionTranslation,
    hasDevicesAssigned,
} from './use-delete-eap-setting.business';
import { DELETE_MODAL_KEY } from './use-delete-eap-setting.constants';
import { UseDeleteEapSettingProps } from './delete-eap-setting.types';

export const useDeleteEapSetting = ({ onDeleted }: UseDeleteEapSettingProps) => {
    const popupManager = usePopupManager();
    const { t } = useTranslation();

    const getDeleteDescription = (eapSettings: EapSettings[]): React.ReactNode => {
        const { key, params } = getDeleteDescriptionTranslation(eapSettings);

        return <PocTypography variant="subtitle1">{t(key, params)}</PocTypography>;
    };
    const { mutateAsync: deleteSelectedEapSettings } = usePocMutation({
        mutationFn: (eapSettings: EapSettings[]) =>
            executeRequest(() =>
                deleteEapSettings(eapSettings.map((es) => es.id))
                    .then(() => {
                        showSuccessNotification(t(DELETE_MODAL_KEY.notification.success));
                        updateQuery(settingsKeys.eapSettings());
                        onDeleted();
                    })
                    .catch(() => {
                        showErrorNotification(t(DELETE_MODAL_KEY.notification.error));
                    })
            ),
    });

    const openDeleteConfirmation = (eapSettings: EapSettings[]) => {
        if (hasDevicesAssigned(eapSettings)) {
            const { key, params } = getPreventDeleteDescriptionTranslation(eapSettings);
            return popupManager.open(PreventModalComponent, {
                title: <DeleteEapSettingHeader eapSettings={eapSettings} />,
                children: <PocTypography variant="subtitle1">{t(key, params)}</PocTypography>,
            });
        }

        popupManager.open(ConfirmationModalComponent, {
            title: <DeleteEapSettingHeader eapSettings={eapSettings} />,
            children: getDeleteDescription(eapSettings),
            buttonText: t(DELETE_MODAL_KEY.button),
            actionOnClick: () => deleteSelectedEapSettings(eapSettings),
        });
    };

    return { openDeleteConfirmation };
};
